import type {ReactElement} from 'react';
import React from 'react';
import {ThumbnailItem, ThumbnailItemSelectionState} from '@Components/thumbnail-item';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Thumbnail} from '@Components/thumbnail';
import {ThumbnailType} from '@Components/thumbnail/thumbnail.types';
import unorderedListSvg from '@Assets/postermaker/unordered-bullets.svg';
import {UNORDERED_LIST_STYLE} from '@PosterWhiteboard/items/text-item/text-list';
import {ContextualMenuHorizontalScrollingSection} from '@Components/contextual-menu/components/contextual-menu-horizontal-scrolling-section';
import styles from './base-unordered-list-thumbnails.module.scss';

interface BaseUnorderedListThumbnailsProps {
  className?: string;
  onClickNone(): void;
  onClickListOption(type: UNORDERED_LIST_STYLE): void;
  selectedId: UNORDERED_LIST_STYLE | null;
}

const UnorderedListOptions = [
  UNORDERED_LIST_STYLE.CIRCLE_1,
  UNORDERED_LIST_STYLE.CIRCLE_2,
  UNORDERED_LIST_STYLE.CIRCLE_3,
  UNORDERED_LIST_STYLE.SQUARE_1,
  UNORDERED_LIST_STYLE.SQUARE_2,
  UNORDERED_LIST_STYLE.SQUARE_3,
  UNORDERED_LIST_STYLE.SQUARE_4,
  UNORDERED_LIST_STYLE.ARROW_1,
  UNORDERED_LIST_STYLE.ARROW_2,
  UNORDERED_LIST_STYLE.ARROW_3,
  UNORDERED_LIST_STYLE.TICK_1,
  UNORDERED_LIST_STYLE.TICK_2,
  UNORDERED_LIST_STYLE.STAR,
  UNORDERED_LIST_STYLE.EDIT,
  UNORDERED_LIST_STYLE.FLOWER,
  UNORDERED_LIST_STYLE.AESTERISK,
  UNORDERED_LIST_STYLE.LEAF,
];

export function BaseUnorderedListThumbnails({className = '', ...props}: BaseUnorderedListThumbnailsProps): ReactElement | null {
  const getStylesForType = (type: UNORDERED_LIST_STYLE): string => {
    switch (type) {
      case UNORDERED_LIST_STYLE.CIRCLE_1:
        return styles.bulletCircle1;
      case UNORDERED_LIST_STYLE.CIRCLE_2:
        return styles.bulletCircle2;
      case UNORDERED_LIST_STYLE.CIRCLE_3:
        return styles.bulletCircle3;
      case UNORDERED_LIST_STYLE.SQUARE_1:
        return styles.bulletSquare1;
      case UNORDERED_LIST_STYLE.SQUARE_2:
        return styles.bulletSquare2;
      case UNORDERED_LIST_STYLE.SQUARE_3:
        return styles.bulletSquare3;
      case UNORDERED_LIST_STYLE.SQUARE_4:
        return styles.bulletSquare4;
      case UNORDERED_LIST_STYLE.ARROW_1:
        return styles.bulletArrowOne;
      case UNORDERED_LIST_STYLE.ARROW_2:
        return styles.bulletArrow2;
      case UNORDERED_LIST_STYLE.ARROW_3:
        return styles.bulletArrow3;
      case UNORDERED_LIST_STYLE.TICK_1:
        return styles.bulletTick1;
      case UNORDERED_LIST_STYLE.TICK_2:
        return styles.bulletTick2;
      case UNORDERED_LIST_STYLE.STAR:
        return styles.bulletStar;
      case UNORDERED_LIST_STYLE.EDIT:
        return styles.bulletEdit;
      case UNORDERED_LIST_STYLE.FLOWER:
        return styles.bulletFlower;
      case UNORDERED_LIST_STYLE.AESTERISK:
        return styles.bulletAesterisk;
      case UNORDERED_LIST_STYLE.LEAF:
        return styles.bulletLeaf;
      default:
        return '';
    }
  };

  const getThumbnails = (): ReactElement[] => {
    return UnorderedListOptions.map((option): ReactElement => {
      return (
        <ThumbnailItem
          key={option}
          className={styles.item}
          thumbnailContainerClassName={styles.hiddenOverflow}
          thumbnail={
            <div className={styles.thumbContainer}>
              <Thumbnail
                data={{
                  type: ThumbnailType.IMAGE,
                  imageSrc: unorderedListSvg as string,
                }}
                className={`${styles.baseThumbnails} ${props?.selectedId === option ? styles.selectedFilter : ''} ${getStylesForType(option)}`}
              />
            </div>
          }
          isSelected={props?.selectedId === option}
          selectionState={ThumbnailItemSelectionState.BORDER}
          textClassName="content-body spacing-m-t-1"
          onClick={(): void => {
            props.onClickListOption(option);
          }}
        />
      );
    });
  };

  const getNoneThumbnail = (): ReactElement => {
    return (
      <ThumbnailItem
        key="ordered-list-none"
        className={styles.item}
        thumbnail={<Icon icon="icon-ban" size={IconSize.SIZE_ICON_24} />}
        isSelected={props.selectedId === null}
        selectionState={ThumbnailItemSelectionState.BORDER}
        onClick={(): void => {
          props.onClickNone();
        }}
      />
    );
  };

  return (
    <ContextualMenuHorizontalScrollingSection className={`${styles.list} ${className} flex-h-row`}>
      <>
        {getNoneThumbnail()}
        {getThumbnails()}
      </>
    </ContextualMenuHorizontalScrollingSection>
  );
}
